import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CloudStatus, SharingState } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { describeError } from "@/data/api";
import { invalidateContainers, qk } from "@/data/cache";
import { useAccount, useSharing } from "@/data/cloud";
import { useDesk, useMinuteClock } from "@/data/context";
import { formatTimestamp, localZone } from "@/lib/dates";
import { SignInForm } from "@/features/pairing/SignInScreen";
import { ShareDialog } from "@/features/containers/ShareDialog";
import { Row, Section } from "./parts";

function syncedAgo(iso: string, now: number): string {
  const mins = Math.floor((now - Date.parse(iso)) / 60_000);
  if (mins < 1) return "Synced just now";
  if (mins < 60) return `Synced ${mins} min ago`;
  return `Synced ${formatTimestamp(iso, localZone())}`;
}

function syncText(s: CloudStatus, now: number): string {
  switch (s.state) {
    case "syncing":
      return "Syncing…";
    case "offline":
      return "Offline";
    case "update_required":
      return "Update Desk to keep syncing";
    case "error":
      return s.lastError ?? "Sync failed";
    default:
      if (s.pending > 0) return `${s.pending} ${s.pending === 1 ? "change" : "changes"} waiting`;
      return s.lastSyncAt ? syncedAgo(s.lastSyncAt, now) : "Up to date";
  }
}

/** Settings → Desk cloud. On the Mac the local API signs in; on the web the window itself is signed in. */
export function CloudSection() {
  const { api, signOut } = useDesk();
  const qc = useQueryClient();
  const account = useAccount();
  const now = useMinuteClock();
  const [signingIn, setSigningIn] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState<"merge" | "download" | null>(null);

  const setStatus = (s: CloudStatus) => void qc.setQueryData(qk.cloud, s);
  const run = async (fn: () => Promise<void>, failure: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(failure, { description: describeError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (account.kind === "cloud") {
    return (
      <Section title="Account">
        <Row label={account.email ?? "Signed in"}>
          <Button size="sm" variant="outline" onClick={signOut}>
            Sign out
          </Button>
        </Row>
      </Section>
    );
  }

  const status = account.status;
  if (!status) return null;
  const needsChoice = status.state === "needs_choice";

  return (
    <Section title="Desk cloud">
      {status.state === "signed_out" ? (
        <Row label="Sync and sharing" hint={status.lastError ?? "Keep this Mac, your iPhone and the web app in sync, and share areas."}>
          <Button size="sm" variant="outline" onClick={() => setSigningIn(true)}>
            Sign in…
          </Button>
        </Row>
      ) : (
        <>
          <Row label={status.email ?? "Signed in"}>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmSignOut(true)}>
              Sign out…
            </Button>
          </Row>
          {needsChoice ? (
            <Row label="Sync" hint="Choose how to combine this Mac with your cloud data." error>
              <Button size="sm" variant="default" onClick={() => setChoosing(true)}>
                Choose…
              </Button>
            </Row>
          ) : (
            <Row label="Sync" hint={status.state === "error" || status.state === "offline" ? status.lastError ?? undefined : undefined} error={status.state === "error"}>
              <span className="text-[13px] tabular text-muted-foreground">{syncText(status, now)}</span>
              <Button
                size="sm"
                variant="outline"
                disabled={busy || status.state === "syncing"}
                onClick={() => void run(async () => setStatus(await api.cloudSync()), "Couldn't sync")}
              >
                Sync now
              </Button>
            </Row>
          )}
        </>
      )}

      <Dialog open={signingIn} onOpenChange={setSigningIn}>
        <DialogContent className="max-w-[400px]">
          <DialogBody>
            <DialogTitle>Sign in to Desk cloud</DialogTitle>
            <SignInForm
              onCode={async (email) => {
                await api.cloudSignIn(email);
              }}
              onVerify={async (email, code) => {
                const s = await api.cloudVerify(email, code);
                setStatus(s);
                setSigningIn(false);
                if (s.state === "needs_choice") setChoosing(true);
                else toast("Signed in to Desk cloud");
                void qc.invalidateQueries({ queryKey: qk.sharing });
              }}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog open={choosing || false} onOpenChange={setChoosing}>
        <DialogContent className="max-w-[460px]">
          <DialogBody>
            <DialogTitle>This Mac and Desk cloud both have tasks</DialogTitle>
            <DialogDescription>
              Merge keeps everything from both. Use cloud data replaces what's on this Mac with what's in Desk cloud.
            </DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  setLinking("download");
                  try {
                    setStatus(await api.cloudLink("download"));
                    setChoosing(false);
                    await invalidateContainers(qc);
                  } finally {
                    setLinking(null);
                  }
                }, "Couldn't sync")
              }
            >
              {linking === "download" ? "Replacing…" : "Use cloud data"}
            </Button>
            <Button
              variant="default"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  setLinking("merge");
                  try {
                    setStatus(await api.cloudLink("merge"));
                    setChoosing(false);
                    await invalidateContainers(qc);
                  } finally {
                    setLinking(null);
                  }
                }, "Couldn't sync")
              }
            >
              {linking === "merge" ? "Merging…" : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmSignOut} onOpenChange={setConfirmSignOut}>
        <DialogContent className="max-w-[420px]">
          <DialogBody>
            <DialogTitle>Sign out of Desk cloud?</DialogTitle>
            <DialogDescription>This Mac keeps its tasks and stops syncing. Shared areas stop updating here.</DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmSignOut(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  setStatus(await api.cloudSignOut());
                  setConfirmSignOut(false);
                  qc.removeQueries({ queryKey: qk.sharing });
                }, "Couldn't sign out")
              }
            >
              Sign out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

/** Settings → Shared areas: invitations, and areas shared with or by me. */
export function SharingSection() {
  const { api } = useDesk();
  const qc = useQueryClient();
  const { signedIn, status } = useAccount();
  const { data, error } = useSharing();
  const [managing, setManaging] = useState<{ id: string; name: string } | null>(null);
  const [leaving, setLeaving] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!signedIn || status?.state === "needs_choice") return null;

  const act = async (fn: () => Promise<SharingState>, failure: string, success?: string) => {
    setBusy(true);
    try {
      qc.setQueryData(qk.sharing, await fn());
      if (success) toast(success);
      await invalidateContainers(qc);
    } catch (e) {
      toast.error(failure, { description: describeError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Shared areas">
      {!data && <p className="text-[13.5px] text-muted-foreground">{error ? describeError(error) : "Loading…"}</p>}
      {data?.invitations.map((inv) => (
        <Row key={inv.hubId} label={inv.name} hint={`Invited by ${inv.invitedBy}`}>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => api.sharingAction(inv.hubId, "decline"), "Couldn't decline")}>
            Decline
          </Button>
          <Button
            size="sm"
            variant="default"
            disabled={busy}
            onClick={() => void act(() => api.sharingAction(inv.hubId, "accept"), "Couldn't join", `Joined ${inv.name}`)}
          >
            Accept
          </Button>
        </Row>
      ))}
      {data?.areas.map((a) => {
        const others = a.members.filter((m) => m.role !== "owner" && m.email !== a.owner);
        const hint =
          a.role === "owner"
            ? others.length
              ? `With ${others.map((m) => (m.status === "invited" ? `${m.email} (invited)` : m.email)).join(", ")}`
              : "No one has joined yet"
            : `Shared by ${a.owner}`;
        return (
          <Row key={a.areaId} label={a.name} hint={hint}>
            {a.role === "owner" ? (
              <Button size="sm" variant="outline" onClick={() => setManaging({ id: a.areaId, name: a.name })}>
                Manage…
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setLeaving({ id: a.areaId, name: a.name })}>
                Leave…
              </Button>
            )}
          </Row>
        );
      })}
      {data && !data.areas.length && !data.invitations.length && (
        <p className="text-[13.5px] text-muted-foreground">Share an area from its ⋯ menu.</p>
      )}
      {managing && <ShareDialog areaId={managing.id} areaName={managing.name} open onOpenChange={(o) => !o && setManaging(null)} />}
      <Dialog open={!!leaving} onOpenChange={(o) => !o && setLeaving(null)}>
        <DialogContent className="max-w-[420px]">
          <DialogBody>
            <DialogTitle>Leave {leaving?.name}?</DialogTitle>
            <DialogDescription>It's removed from all your devices. The person who shared it keeps it.</DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLeaving(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                const target = leaving!;
                setLeaving(null);
                void act(() => api.sharingAction(target.id, "leave"), "Couldn't leave", `Left ${target.name}`);
              }}
            >
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
